import { useState } from "react";
import { track } from "../../lib/analytics.ts";
import type { SleepRow } from "../../lib/overview.ts";
import { clock, shortDuration, weekdayDay } from "../../lib/time.ts";
import { cn } from "../../lib/utils.ts";

/** The noon-to-noon axis, shared with the report's static chart. */
export const TICKS = [
  { at: 0, label: "noon" },
  { at: 0.25, label: "6 pm" },
  { at: 0.5, label: "12 am" },
  { at: 0.75, label: "6 am" },
  { at: 1, label: "noon" },
];
const GRID = "grid grid-cols-[48px_minmax(0,1fr)_56px] gap-x-2";

function describe(row: SleepRow): string {
  const night = `${weekdayDay(row.start)} night`;
  if (!row.segments.length) return `${night} · no sleep logged`;
  const l = row.longest;
  const longest = l
    ? `longest ${shortDuration(l.ms)} (${clock(l.at)}–${l.endedAt ? clock(l.endedAt) : "now"})`
    : null;
  return [night, longest, `${shortDuration(row.totalMs)} asleep`].filter(Boolean).join(" · ");
}

/**
 * The sleep window over time: one row per night, noon to noon, so the main
 * stretch reads as one unbroken bar and its drift (earlier bedtime, later
 * wake) shows down the column. The longest stretch is solid, other naps are
 * a lighter wash; the right column carries the longest stretch as a number.
 */
export function SleepChart({ rows }: { rows: SleepRow[] }) {
  // Before midnight the current row is mostly empty: lead with last night.
  const preferred = rows[0]?.nowAt !== null && (rows[0]?.nowAt ?? 1) < 0.5 ? 1 : 0;
  const [picked, setPicked] = useState<number | null>(null);
  const selected = Math.min(picked ?? preferred, rows.length - 1);
  const best = Math.max(0, ...rows.map((r) => r.longest?.ms ?? 0));

  if (!rows.length)
    return <p className="py-3 text-[14px] italic opacity-60">No sleep logged yet.</p>;
  return (
    <figure>
      <figcaption className="min-h-[2.6em] text-[13px] leading-snug" aria-live="polite">
        {describe(rows[selected]!)}
      </figcaption>
      <div
        className={cn(GRID, "mt-2 text-[10px] uppercase tracking-[0.06em] opacity-55")}
        aria-hidden="true"
      >
        <span />
        <span className="relative h-3">
          {TICKS.map((t) => (
            <span
              key={t.at}
              className={cn(
                "absolute top-0 whitespace-nowrap",
                t.at === 0 ? "left-0" : t.at === 1 ? "right-0" : "-translate-x-1/2",
              )}
              style={t.at > 0 && t.at < 1 ? { left: `${t.at * 100}%` } : undefined}
            >
              {t.label}
            </span>
          ))}
        </span>
        <span className="text-right">Longest</span>
      </div>
      <ul className="mt-1">
        {rows.map((row, i) => (
          <li key={row.start.getTime()}>
            <button
              type="button"
              className={cn(
                GRID,
                "min-h-[26px] w-full items-center rounded-sm text-left",
                i === selected ? "bg-rule" : "hover:bg-rule",
              )}
              aria-label={describe(row)}
              aria-pressed={i === selected}
              onPointerEnter={(e) => e.pointerType === "mouse" && setPicked(i)}
              onFocus={() => setPicked(i)}
              onClick={() => {
                setPicked(i);
                track("chart day selected", { chart: "sleep" });
              }}
            >
              <span className={cn("pl-1 text-[12px] tabular-nums", i !== selected && "opacity-70")}>
                {weekdayDay(row.start)}
              </span>
              <span className="relative h-3.5">
                {[0.25, 0.5, 0.75].map((at) => (
                  <span
                    key={at}
                    className="absolute inset-y-0 w-px bg-rule"
                    style={{ left: `${at * 100}%` }}
                  />
                ))}
                <span className="absolute inset-x-0 bottom-0 h-px bg-rule" />
                {row.segments.map((s) => (
                  <span
                    key={s.id}
                    className={cn(
                      "absolute inset-y-0 min-w-[2px] rounded-[1px] bg-chart-sleep",
                      !s.main && "opacity-45",
                      s.open && "animate-pulse",
                    )}
                    style={{ left: `${s.start * 100}%`, width: `${(s.end - s.start) * 100}%` }}
                  />
                ))}
                {row.nowAt !== null ? (
                  <span
                    className="absolute -inset-y-1 w-px bg-ink"
                    style={{ left: `${row.nowAt * 100}%` }}
                    title="Now"
                  />
                ) : null}
              </span>
              <span
                className={cn(
                  "pr-1 text-right text-[12px] tabular-nums",
                  row.longest && row.longest.ms === best ? "heading" : "opacity-70",
                )}
              >
                {row.longest ? shortDuration(row.longest.ms) : "—"}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </figure>
  );
}
